<template>
  <div class="quote-page">
    <!-- ==================== SKELETON LOADING STATE ==================== -->
    <template v-if="loading">
      <div class="skeleton-container">
        <!-- Skeleton Header -->
        <div class="skeleton-header-section">
          <div class="skeleton-header-content">
            <div class="skeleton-header-text">
              <div class="skeleton-title"></div>
              <div class="skeleton-subtitle"></div>
            </div>
            <div class="skeleton-stats-card">
              <div class="skeleton-stats-icon"></div>
              <div class="skeleton-stats-info">
                <div class="skeleton-stats-number"></div>
                <div class="skeleton-stats-label"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Skeleton Project Selector -->
        <div class="skeleton-selector-section">
          <div class="skeleton-selector-title"></div>
          <div class="skeleton-project-grid">
            <div
              v-for="n in 4"
              :key="'skeleton-project-' + n"
              class="skeleton-project-card"
            >
              <div class="skeleton-project-icon"></div>
              <div class="skeleton-project-name"></div>
            </div>
          </div>
        </div>

        <!-- Skeleton Widget Area -->
        <div class="skeleton-widget-area">
          <div class="skeleton-widget-placeholder"></div>
        </div>
      </div>
    </template>

    <!-- ==================== MAIN CONTENT ==================== -->
    <template v-else>
      <!-- Header Section with Stats -->
      <div class="quote-header-section">
        <div class="quote-header-content">
          <div class="quote-header-text">
            <h2 class="quote-title">Nueva {{ quoteTextSingular }}</h2>
            <p class="quote-subtitle">
              Selecciona el proyecto y simula tu próxima inversión inmobiliaria.
            </p>
          </div>
          <div class="quote-stats-card" v-if="projects.length > 0">
            <div class="stats-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path
                  d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M9 7H7V9H9V7Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M17 7H11"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
                <path
                  d="M9 11H7V13H9V11Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M17 11H11"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
                <path
                  d="M9 15H7V17H9V15Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M17 15H11"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
            </div>
            <div class="stats-info">
              <span class="stats-number">{{ projects.length }}</span>
              <span class="stats-label">
                {{ projects.length === 1 ? "Proyecto" : "Proyectos" }}
                disponibles
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Project Selector Section -->
      <div class="project-selector-section">
        <div class="selector-header">
          <h3 class="selector-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 9.5L12 4L21 9.5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M19 13V19.4C19 19.7314 18.7314 20 18.4 20H5.6C5.26863 20 5 19.7314 5 19.4V13"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Selecciona un proyecto
          </h3>
          <span class="selector-hint" v-if="!projectId">
            Elige el proyecto que deseas cotizar
          </span>
          <span class="selector-hint selected" v-else>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 12L11 14L15 10"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                stroke-width="1.5"
              />
            </svg>
            Proyecto seleccionado
          </span>
        </div>

        <!-- Empty State - No Projects -->
        <div v-if="projects.length === 0" class="empty-state">
          <div class="empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 9.5L12 4L21 9.5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M19 13V19.4C19 19.7314 18.7314 20 18.4 20H5.6C5.26863 20 5 19.7314 5 19.4V13"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <h3 class="empty-title">No hay proyectos disponibles</h3>
          <p class="empty-text">
            Actualmente no hay proyectos habilitados para cotizar. Por favor,
            intenta más tarde.
          </p>
        </div>

        <!-- Project Cards Grid -->
        <div v-else class="project-grid">
          <div
            v-for="project in projects"
            :key="project.value"
            class="project-card"
            :class="{ active: projectId === project.value }"
            @click="selectProject(project.value)"
          >
            <div class="project-card-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 9.5L12 4L21 9.5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M19 13V19.4C19 19.7314 18.7314 20 18.4 20H5.6C5.26863 20 5 19.7314 5 19.4V13"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M9 20V14H15V20"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div class="project-card-content">
              <span class="project-name">{{ project.text }}</span>
              <span class="project-action">
                {{
                  projectId === project.value ? "Seleccionado" : "Seleccionar"
                }}
              </span>
            </div>
            <div class="project-check" v-if="projectId === project.value">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 13L9 17L19 7"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>

        <!-- Alternative Dropdown for many projects -->
        <div class="selector-dropdown-wrapper" v-if="projects.length > 6">
          <label class="dropdown-label">O selecciona desde la lista:</label>
          <b-form-select
            v-model="projectId"
            :options="projects"
            @change="onDropdownChange"
            class="custom-project-select"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                Selecciona un proyecto...
              </b-form-select-option>
            </template>
          </b-form-select>
        </div>
      </div>

      <!-- Quote Widget Section -->
      <div class="quote-widget-section" v-if="projectId" ref="quoteWidget">
        <div class="widget-header">
          <h3 class="widget-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 7H6C4.89543 7 4 7.89543 4 9V18C4 19.1046 4.89543 20 6 20H18C19.1046 20 20 19.1046 20 18V9C20 7.89543 19.1046 7 18 7H15"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M12 4V14M12 14L15 11M12 14L9 11"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Cotizador
          </h3>
          <span class="widget-project-name">
            {{ getSelectedProjectName }}
          </span>
        </div>
        <div class="widget-container">
          <div id="widget" class="quote-widget-container"></div>
        </div>
      </div>

      <!-- Initial State - No project selected -->
      <div class="initial-state" v-else-if="projects.length > 0">
        <div class="initial-icon">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 15L21 21"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M10 17C13.866 17 17 13.866 17 10C17 6.13401 13.866 3 10 3C6.13401 3 3 6.13401 3 10C3 13.866 6.13401 17 10 17Z"
              stroke="currentColor"
              stroke-width="1.5"
            />
          </svg>
        </div>
        <h3 class="initial-title">Selecciona un proyecto</h3>
        <p class="initial-text">
          Elige un proyecto de la lista para comenzar a simular tu inversión y
          recibir una {{ quoteTextSingular }} personalizada.
        </p>
        <div class="initial-steps">
          <div class="step-item">
            <span class="step-number">1</span>
            <span class="step-text">Selecciona el proyecto</span>
          </div>
          <div class="step-arrow">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 12H19M19 12L12 5M19 12L12 19"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="step-item">
            <span class="step-number">2</span>
            <span class="step-text">Simula tu inversión</span>
          </div>
          <div class="step-arrow">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 12H19M19 12L12 5M19 12L12 19"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="step-item">
            <span class="step-number">3</span>
            <span class="step-text">Recibe tu {{ quoteTextSingular }}</span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "quote",
  data() {
    return {
      projectId: null,
      showQuote: false,
      quoteWidget: null,
      initialLoading: true,
      minLoadingComplete: false,
    };
  },
  mounted() {
    // Mínimo tiempo de skeleton para transición suave
    setTimeout(() => {
      this.minLoadingComplete = true;
    }, 400);
  },
  computed: {
    loading() {
      // Mostrar skeleton si los proyectos no están cargados O si no ha pasado el tiempo mínimo
      const projectsNotLoaded =
        this.$store.getters.getProjects === null ||
        this.$store.getters.getProjects === undefined;
      return projectsNotLoaded || !this.minLoadingComplete;
    },
    customer() {
      return this.$store.getters.getCustomer;
    },
    realEstate() {
      const fromStore = this.$store.getters.getCustomer;
      if (fromStore) return fromStore;

      const fromRoute =
        this.$route && this.$route.params && this.$route.params.customer;
      if (fromRoute) return fromRoute;

      if (
        typeof window !== "undefined" &&
        window.location &&
        window.location.pathname
      ) {
        const segments = window.location.pathname.split("/").filter(Boolean);
        const appIndex = segments.indexOf("app");
        if (appIndex !== -1 && segments[appIndex + 1])
          return segments[appIndex + 1];
        if (segments[0]) return segments[0];
      }

      return null;
    },
    gcVersion() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.gc_version
        : 1;
    },
    country() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.country
        : 1;
    },
    quoteTextSingular() {
      return this.country == 0 ? "Proforma" : "Cotización";
    },
    projects() {
      if (this.$store.getters.getProjects) {
        return this.$store.getters.getProjects.reduce(function (
          newData,
          project
        ) {
          if (project.activoCotizadorWeb) {
            if (project.nombreProyecto != "Marcha Blanca") {
              newData.push({ value: project.id, text: project.nombreProyecto });
            }
          }
          return newData;
        },
        []);
      } else {
        return [];
      }
    },
    getSelectedProjectName() {
      if (!this.projectId) return "";
      const project = this.projects.find((p) => p.value === this.projectId);
      return project ? project.text : "";
    },
  },
  methods: {
    selectProject(projectValue) {
      this.projectId = projectValue;
      // Esperar a que el DOM se actualice antes de cargar el widget y hacer scroll
      this.$nextTick(() => {
        setTimeout(() => {
          this.reload();
          this.scrollToWidget();
        }, 150);
      });
    },
    onDropdownChange() {
      // El v-model ya actualiza projectId, solo necesitamos reload y scroll
      this.$nextTick(() => {
        setTimeout(() => {
          this.reload();
          this.scrollToWidget();
        }, 150);
      });
    },
    scrollToWidget() {
      if (this.$refs.quoteWidget) {
        this.$refs.quoteWidget.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    },
    reload() {
      let self = this;
      document.getElementById("widget").innerHTML = "";
      setTimeout(function () {
        document.getElementById(
          "widget"
        ).innerHTML = `<mobysuite-quote project-id="${
          self.projectId
        }" version="${self.gcVersion}" real-estate="${
          self.realEstate || ""
        }"></mobysuite-quote>`;
        let script = document.createElement("script");
        script.src = "https://cdn.mobysuite.com/quote/js/app.js";
        document.getElementById("widget").appendChild(script);
      }, 500);
    },
  },
};
</script>

<style scoped>
/* ===== Quote Page Premium Styles ===== */
.quote-page {
  /* Variables dinámicas basadas en el tema de la inmobiliaria */
  --theme-primary: var(--color-btn, #3b82f6);
  --theme-primary-hover: var(--color-btn-a, #2563eb);
  --theme-primary-text: var(--color-btn-text, #ffffff);
  --theme-accent: var(--color-a, #3b82f6);

  background: transparent;
  min-height: 100vh;
}

/* ===== SKELETON LOADING STYLES ===== */
.skeleton-container {
  animation: skeletonFadeIn 0.3s ease;
}

@keyframes skeletonFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.skeleton-header-section {
  margin-bottom: 24px;
}

.skeleton-header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;
}

.skeleton-header-text {
  flex: 1;
  min-width: 280px;
}

.skeleton-title {
  width: 220px;
  height: 32px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
  margin-bottom: 12px;
}

.skeleton-subtitle {
  width: 350px;
  max-width: 100%;
  height: 20px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
}

.skeleton-stats-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 24px;
}

.skeleton-stats-icon {
  width: 52px;
  height: 52px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
}

.skeleton-stats-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.skeleton-stats-number {
  width: 40px;
  height: 28px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
}

.skeleton-stats-label {
  width: 120px;
  height: 16px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-selector-section {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid #e5e7eb;
}

.skeleton-selector-title {
  width: 180px;
  height: 24px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
  margin-bottom: 20px;
}

.skeleton-project-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.skeleton-project-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px;
  background: #f9fafb;
  border-radius: 12px;
}

.skeleton-project-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
  margin-bottom: 12px;
}

.skeleton-project-name {
  width: 100px;
  height: 16px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-widget-area {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #e5e7eb;
}

.skeleton-widget-placeholder {
  height: 400px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ===== HEADER SECTION ===== */
.quote-header-section {
  padding: 0;
  margin-bottom: 24px;
  animation: headerSectionFadeIn 0.5s ease;
}

@keyframes headerSectionFadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.quote-header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;
}

.quote-header-text {
  flex: 1;
  min-width: 280px;
}

.quote-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px;
  animation: titleSlideIn 0.4s ease 0.1s backwards;
}

@keyframes titleSlideIn {
  from {
    opacity: 0;
    transform: translateX(-15px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.quote-subtitle {
  font-size: 0.9375rem;
  color: #6b7280;
  margin: 0;
  max-width: 500px;
  line-height: 1.5;
  animation: subtitleFadeIn 0.4s ease 0.2s backwards;
}

@keyframes subtitleFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Stats Card */
.quote-stats-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 24px;
  animation: statsCardSlide 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s
    backwards;
}

@keyframes statsCardSlide {
  from {
    opacity: 0;
    transform: translateX(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

.stats-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  background: var(--theme-primary);
  border-radius: 12px;
  color: white;
  animation: iconPulse 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s backwards;
}

@keyframes iconPulse {
  from {
    opacity: 0;
    transform: scale(0);
  }
  60% {
    transform: scale(1.15);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.stats-info {
  display: flex;
  flex-direction: column;
}

.stats-number {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
  animation: numberCount 0.6s ease 0.6s backwards;
}

@keyframes numberCount {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stats-label {
  font-size: 0.8125rem;
  color: #6b7280;
  margin-top: 4px;
  text-transform: capitalize;
}

/* ===== PROJECT SELECTOR SECTION ===== */
.project-selector-section {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  animation: selectorFadeIn 0.4s ease 0.2s backwards;
}

@keyframes selectorFadeIn {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.selector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.selector-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.selector-title svg {
  color: var(--theme-primary);
}

.selector-hint {
  font-size: 0.8125rem;
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 6px;
}

.selector-hint.selected {
  color: #16a34a;
  background: rgba(34, 197, 94, 0.08);
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 500;
}

/* Project Grid */
.project-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  width: 100%;
  max-width: 100%;
  overflow: visible;
  padding-top: 4px;
}

.project-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  background: #f9fafb;
  border: 2px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  animation: cardFadeIn 0.4s ease backwards;
}

.project-card:nth-child(1) {
  animation-delay: 0.1s;
}
.project-card:nth-child(2) {
  animation-delay: 0.15s;
}
.project-card:nth-child(3) {
  animation-delay: 0.2s;
}
.project-card:nth-child(4) {
  animation-delay: 0.25s;
}
.project-card:nth-child(5) {
  animation-delay: 0.3s;
}
.project-card:nth-child(6) {
  animation-delay: 0.35s;
}

@keyframes cardFadeIn {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.project-card:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
  transform: translateY(-2px);
}

.project-card.active {
  background: rgba(59, 130, 246, 0.05);
  background: var(--theme-primary-bg, rgba(59, 130, 246, 0.05));
  border-color: var(--theme-primary);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.project-card.active:hover {
  transform: translateY(0);
}

.project-card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: #ffffff;
  border-radius: 10px;
  color: #6b7280;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.project-card.active .project-card-icon {
  background: var(--theme-primary);
  color: var(--theme-primary-text);
}

.project-card-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.project-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  word-break: break-word;
  overflow-wrap: break-word;
}

.project-action {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 2px;
  transition: color 0.2s ease;
}

.project-card:hover .project-action {
  color: #6b7280;
}

.project-card.active .project-action {
  color: var(--theme-primary);
}

.project-check {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: var(--theme-primary);
  border-radius: 50%;
  color: var(--theme-primary-text);
  animation: checkBounce 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes checkBounce {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

/* Dropdown Alternative */
.selector-dropdown-wrapper {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.dropdown-label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 10px;
}

.custom-project-select {
  width: 100%;
  max-width: 400px;
  height: 48px;
  padding: 0 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.9375rem;
  color: #1f2937;
  background-color: #ffffff;
  transition: all 0.2s ease;
  cursor: pointer;
}

.custom-project-select:focus {
  outline: none;
  border-color: var(--theme-primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* ===== QUOTE WIDGET SECTION ===== */
.quote-widget-section {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  animation: widgetFadeIn 0.4s ease;
}

@keyframes widgetFadeIn {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.widget-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.widget-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.widget-title svg {
  color: var(--theme-primary);
}

.widget-project-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--theme-primary);
  background: rgba(59, 130, 246, 0.08);
  background: var(--theme-primary-bg, rgba(59, 130, 246, 0.08));
  padding: 6px 14px;
  border-radius: 20px;
}

.widget-container {
  padding: 24px;
}

.quote-iframe-container {
  max-height: 750px;
  height: 100vh;
  border-radius: 8px;
  overflow: hidden;
}

.quote-iframe-container iframe {
  border-radius: 8px;
}

.quote-widget-container {
  min-height: 400px;
}

/* ===== EMPTY STATE ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
}

.empty-icon {
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border-radius: 20px;
  color: #9ca3af;
  margin-bottom: 20px;
}

.empty-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
}

.empty-text {
  font-size: 0.875rem;
  color: #6b7280;
  max-width: 360px;
}

/* ===== INITIAL STATE ===== */
.initial-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  text-align: center;
  animation: initialFadeIn 0.4s ease 0.3s backwards;
}

@keyframes initialFadeIn {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.initial-icon {
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border-radius: 28px;
  color: #9ca3af;
  margin-bottom: 24px;
}

.initial-title {
  font-size: 1.375rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 10px;
}

.initial-text {
  font-size: 0.9375rem;
  color: #6b7280;
  max-width: 420px;
  line-height: 1.6;
  margin-bottom: 32px;
}

/* Steps */
.initial-steps {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  background: #f9fafb;
  border-radius: 10px;
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: var(--theme-primary);
  color: var(--theme-primary-text);
  border-radius: 50%;
  font-size: 0.8125rem;
  font-weight: 600;
}

.step-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.step-arrow {
  color: #d1d5db;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 1200px) {
  .skeleton-project-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 992px) {
  .skeleton-project-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .project-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .quote-header-content {
    flex-direction: column;
    gap: 16px;
  }

  .quote-stats-card {
    width: 100%;
  }

  .selector-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .initial-steps {
    flex-direction: column;
    gap: 12px;
  }

  .step-arrow {
    transform: rotate(90deg);
  }

  .widget-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}

@media (max-width: 576px) {
  .quote-title {
    font-size: 1.5rem;
  }

  .quote-stats-card {
    padding: 14px 18px;
  }

  .stats-icon {
    width: 44px;
    height: 44px;
  }

  .stats-icon svg {
    width: 22px;
    height: 22px;
  }

  .stats-number {
    font-size: 1.5rem;
  }

  .project-selector-section {
    padding: 18px;
    overflow: hidden;
  }

  .skeleton-project-grid {
    grid-template-columns: 1fr;
  }

  .project-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .project-card {
    padding: 14px 16px;
    width: 100%;
    box-sizing: border-box;
  }

  .project-card-icon {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
  }

  .project-card-icon svg {
    width: 20px;
    height: 20px;
  }

  .project-name {
    font-size: 0.875rem;
  }

  .project-action {
    font-size: 0.6875rem;
  }

  .project-check {
    width: 20px;
    height: 20px;
    top: 10px;
    right: 10px;
  }

  .project-check svg {
    width: 12px;
    height: 12px;
  }

  .initial-state {
    padding: 48px 20px;
  }

  .initial-icon {
    width: 100px;
    height: 100px;
    border-radius: 20px;
  }

  .initial-title {
    font-size: 1.25rem;
  }

  .step-item {
    width: 100%;
    justify-content: center;
  }

  .widget-container {
    padding: 16px;
  }
}
</style>
